import { useEffect, useState } from 'react'
import { useDropdown } from '@/hooks/Dropdown-hook'
import { DROPDOWN_TYPES } from '@/utils/constants/dropdownConstants'
import { ACTION_DROPDOWN, EXCEL_ACTION_OPTION, SCHEDULE_DROPDOWN, STORE_SELECTOR_DROPDOWN } from '@/utils/constants/commonConstants'
import { useAppDispatch, useAppSelector } from '@/store'
import { resetBulkModifiers } from '@/store/slices/bulkPointSlice'
import { downloadTemplateFile, TEMPLATE_TYPE } from '@/services/downloadTemplateFile'
import { downloadCSVFiles, getDateWithTimeString } from '@/helpers/shadcn/utils'
import { CreateBulkModifiersSchemas } from '@/services/schema/BulkPointModifiers/CreateBulkModifiersJSONSchemas'

export const useCreateBulkPointModifiers = () => {
  const dispatch = useAppDispatch()
  const bulkModifier = useAppSelector(state => state?.bulkModifiers)
  const [formData, setFormData] = useState(bulkModifier)

  const { dropdownList: dropdownListStoreSelector } = useDropdown({ dropdownType: DROPDOWN_TYPES.StoreSelector })
  const { dropdownList: dropdownListActions } = useDropdown({ dropdownType: DROPDOWN_TYPES.Actions })
  const { dropdownList: dropdownListSchedule } = useDropdown({ dropdownType: DROPDOWN_TYPES.Schedule })

  const [storeSelectorList, setStoreSelectorList] = useState({})
  const [actionsList, setActionsList] = useState({})
  const [scheduleList, setScheduleList] = useState({})

  const { REMOVE_ALL_EXCEPT, REMOVE_NUM_POINTS, ADD_POINTS } = ACTION_DROPDOWN || {}
  const { END_OF_MONTH, END_OF_YEAR, CUSTOM_SCHEDULE } = SCHEDULE_DROPDOWN || {}
  const { EXCEL_UPLOAD } = STORE_SELECTOR_DROPDOWN || {}

  const {
    bulkModifierSetupJsonSchema,
    bulkModifierSetupUISchema,
    rulesJsonSchema,
    rulesUISchema,
    numOfDaysSchema,
    numOfDaysUISchema,
    numOfPointsSchema,
    numOfPointsUISchema,
    scheduleJsonSchema,
    scheduleUISchema,
    startDateJsonSchema,
    startDateUISchema,
    endDateJsonSchema,
    endDateUISchema,
  } = CreateBulkModifiersSchemas

  const handleFormChange = data => {
    setFormData(prev => {
      // Check if storeSelector is being changed
      if (
        Object.prototype.hasOwnProperty.call(data, 'storeSelector') &&
        data.storeSelector !== prev.storeSelector
      ) {
        return {
          ...prev,
          ...data,
          actions: '',
        };
      }
      return {
        ...prev,
        ...data,
      };
    });
  }

  const handleFormReset = () => {
    dispatch(resetBulkModifiers())
  }

  const validateFormData = ({
    bulkModifierName,
    bulkModifierNameValid,
    storeSelector,
    actions,
    numberOfDays,
    numberOfDaysValid,
    numberOfPoints,
    numberOfPointsValid,
    schedule,
    startDate,
    startDateValid,
    endDate,
    endDateValid,
    storeList
  }) => {
    const actionsVal = actions?.toLowerCase()
    const scheduleVal = schedule?.toLowerCase()
    const isExcelUpload = storeSelector && storeSelector.toLowerCase() === EXCEL_UPLOAD;

    const isInValid = [
      !bulkModifierName || bulkModifierName === '' || bulkModifierNameValid,
      !storeSelector || storeSelector === '',
      !actionsVal ||
      actionsVal === '' ||
      (actionsVal === REMOVE_ALL_EXCEPT && (numberOfDays === '' || numberOfDaysValid)) ||
      ((actionsVal === REMOVE_NUM_POINTS || actionsVal === ADD_POINTS) &&
        (numberOfPoints === '' || numberOfPointsValid)),
      !scheduleVal || scheduleVal === '',
      (scheduleVal === CUSTOM_SCHEDULE && (startDate === '' || startDateValid)) ||
      ((scheduleVal === END_OF_MONTH || scheduleVal === END_OF_YEAR) && (endDate === '' || endDateValid)),
      (isExcelUpload && (!storeList || storeList === '')),
    ].some(Boolean)

    return isInValid
  }

  const getScheduleInfo = scheduleVal => {
    const scheduleOptions = {
      [CUSTOM_SCHEDULE]: { scheduleRowClass: 'w-1/2', showStartDate: true, showEndDate: false },
      [END_OF_MONTH]: { scheduleRowClass: 'w-1/2', showStartDate: false, showEndDate: true },
      [END_OF_YEAR]: { scheduleRowClass: 'w-1/2', showStartDate: false, showEndDate: true },
    }

    return scheduleOptions[scheduleVal] || { scheduleRowClass: 'w-1/4', showStartDate: false, showEndDate: false }
  }

  const handleFileDownload = async () => {
    const body = { type: TEMPLATE_TYPE.BULK_POINT_MODIFIER }
    const response = await downloadTemplateFile(body)
    if (!response) return
    const fileName = `BulkPointModifierTemplate_${getDateWithTimeString()}`
    downloadCSVFiles(response, fileName)
  }

  const handleSetStoreSelectorList = (bulkModifierSetupJsonSchema, dropdownListStoreSelector) => {
    if (!dropdownListStoreSelector) return
    if (bulkModifierSetupJsonSchema && dropdownListStoreSelector?.length > 0) {
      let updatedDropdownData = JSON.parse(JSON.stringify(bulkModifierSetupJsonSchema))
      if (updatedDropdownData?.properties?.storeSelector) {
        updatedDropdownData.properties.storeSelector.options = dropdownListStoreSelector.filter(
          opt => opt && opt.displayText
        );
      }
      setStoreSelectorList(updatedDropdownData)
    }
  }



  const handleSetActionsList = (rulesJsonSchema, dropdownListActions, storeSelector) => {
    if (!dropdownListActions) return;
    if (typeof rulesJsonSchema === 'object' && dropdownListActions?.length > 0) {
      let updatedDropdownData = JSON.parse(JSON.stringify(rulesJsonSchema));
      if (updatedDropdownData?.properties?.actions) {

        if (storeSelector && storeSelector.toLowerCase() === 'excel upload') {

          const excelOption = dropdownListActions.find(
            opt =>
              (opt.displayText && opt.displayText === EXCEL_ACTION_OPTION) ||
              (opt.value && opt.value === EXCEL_ACTION_OPTION)
          );
          updatedDropdownData.properties.actions.options = excelOption ? [excelOption] : [];
        } else {
          updatedDropdownData.properties.actions.options = dropdownListActions.filter(
            opt =>
              !(
                (opt.displayText && opt.displayText === EXCEL_ACTION_OPTION) ||
                (opt.value && opt.value === EXCEL_ACTION_OPTION)
              )
          );
        }
      }
      setActionsList(updatedDropdownData);
    }
  }

  const handleSetScheduleList = (scheduleJsonSchema, dropdownListSchedule) => {
    if (!dropdownListSchedule) return
    if (typeof scheduleJsonSchema === 'object' && dropdownListSchedule?.length > 0) {
      let updatedDropdownData = JSON.parse(JSON.stringify(scheduleJsonSchema))
      if (updatedDropdownData?.properties?.schedule) {
        updatedDropdownData.properties.schedule.options = [...dropdownListSchedule]
      }
      setScheduleList(updatedDropdownData)
    }
  }

  useEffect(() => {
    // append  data to storeSelector dropdown
    handleSetStoreSelectorList(bulkModifierSetupJsonSchema, dropdownListStoreSelector)

    // append data to actions dropdown, filtered for Excel Upload
    handleSetActionsList(rulesJsonSchema, dropdownListActions, formData?.storeSelector)

    // append data to schedule dropdown
    handleSetScheduleList(scheduleJsonSchema, dropdownListSchedule)
  }, [
    bulkModifierSetupJsonSchema,
    rulesJsonSchema,
    scheduleJsonSchema,
    dropdownListStoreSelector,
    dropdownListActions,
    dropdownListSchedule,
    formData?.storeSelector
  ])

  return {
    formData,
    setFormData,
    handleFormChange,
    handleFileDownload,
    handleFormReset,
    validateFormData,
    getScheduleInfo,
    bulkModifierSetupJsonSchema: storeSelectorList,
    bulkModifierSetupUISchema,
    rulesJsonSchema: actionsList,
    rulesUISchema,
    numOfDaysSchema,
    numOfDaysUISchema,
    numOfPointsSchema,
    numOfPointsUISchema,
    scheduleJsonSchema: scheduleList,
    scheduleUISchema,
    startDateJsonSchema,
    startDateUISchema,
    endDateJsonSchema,
    endDateUISchema,
  }
}
